<template>
  <div class="menu" data-test="desktop-mega-menu">
    <div
      v-for="(mainItem, mainIdx) in menu"
      :key="mainIdx"
      class="main"
      data-test="desktop-main-item"
      :class="{ expended: currentMainIndex === mainIdx }"
      @mouseenter="onMouseEnterMain(mainIdx)"
      @mouseleave="onMouseLeaveMain()"
    >
      <a
        :href="mainItem.link"
        data-test="desktop-main-link"
        @click="(e) => handleItemClick(e, mainItem)"
      >
        <span>{{ mainItem.text }}</span>
        <svg
          v-if="mainItem.children != null && mainItem.children.length > 0"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M1 0.75L6 5.25L11 0.75" stroke="currentColor" stroke-width="1.5" />
        </svg>
      </a>
      <div
        v-if="
          currentMainIndex === mainIdx && mainItem.children != null && mainItem.children.length > 0
        "
        class="subPanel"
        data-test="desktop-sub-panel"
      >
        <div class="l1-list">
          <a
            v-for="(l1Item, l1Idx) in mainItem.children"
            :key="l1Idx"
            :href="l1Item.link"
            class="l1 item"
            data-test="desktop-l1-link"
            :class="{ selected: currentL1Index === l1Idx }"
            @mouseenter="onMouseEnterL1(l1Idx)"
          >
            {{ l1Item.text }}
          </a>
        </div>
        <template v-if="currentL1Index !== null && hasSubMenu(mainItem.children[currentL1Index])">
          <div
            v-for="slice in getSliceCount(mainItem, currentL1Index)"
            :key="slice"
            class="l2-list"
          >
            <a
              v-for="(l2Item, l2Idx) in getSliceItems(
                mainItem,
                mainItem.children[currentL1Index],
                slice
              )"
              :key="l2Idx"
              :href="l2Item.link"
              class="l2 item"
              data-test="desktop-l2-link"
            >
              {{ l2Item.text }}
            </a>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { MenuItem } from '@/types/menu'

defineProps<{
  menu: MenuItem[]
}>()

function handleItemClick(e: MouseEvent, item: MenuItem) {
  if ((item.link == null || item.link.trim() === '') && item.data?.type === 'url') {
    e.preventDefault()
  }
}

function hasSubMenu(item: MenuItem | undefined) {
  return item?.children != null && item.children.length > 0
}

const currentMainIndex = ref<number | null>(null)
const currentL1Index = ref<number | null>(null)

function onMouseLeaveMain() {
  currentMainIndex.value = null
  currentL1Index.value = null
}

function onMouseEnterMain(idx: number) {
  currentMainIndex.value = idx
  currentL1Index.value = null
}

function onMouseEnterL1(idx: number) {
  currentL1Index.value = idx
}

// Items per column: up to 15, but never more than 3 columns (e.g. Europe).
const MAX_COLUMNS = 3
const MIN_ITEMS_PER_COLUMN = 15

function getSliceLength(l1Item: MenuItem | undefined) {
  const count = l1Item?.children?.length ?? 0
  return Math.max(MIN_ITEMS_PER_COLUMN, Math.ceil(count / MAX_COLUMNS))
}

function getSliceItems(mainItem: MenuItem, l1Item: MenuItem, slice: number) {
  const len = getSliceLength(l1Item)
  return (l1Item.children ?? []).slice((slice - 1) * len, slice * len)
}

function getSliceCount(mainItem: MenuItem, l1Index: number) {
  const l1Item = mainItem.children?.[l1Index]
  return Math.ceil((l1Item?.children?.length ?? 0) / getSliceLength(l1Item))
}
</script>


<style scoped lang="scss">
.menu {
  display: flex;
  font-family: Poppins;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 2.2rem;
  text-align: left;
  cursor: pointer;

  .main {
    padding: 0.8rem 1.6rem;
    position: relative;
    font-size: 1.4rem;
    font-weight: 600;
    line-height: 7rem;

    svg {
      margin-left: 0.6rem;
      transition: 0.25s;
    }

    .subPanel {
      display: none;
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 20;
      border-radius: 1.2rem;
      background-color: white;
      padding: 1rem;
      gap: 2rem;
      box-shadow: 0.5rem 0.6rem 0.6rem 0 #000000;

      .item {
        font-weight: bold;
        border-radius: 0.6rem;
        white-space: nowrap;
        height: 3rem;
        display: flex;
        justify-content: start;
        align-items: center;
        padding: 0 1rem;
        min-width: 8rem;
      }

      .item.selected,
      .item:hover {
        background-color: rgba(76, 167, 113, 0.2);
      }
    }
  }

  .main.expended {
    svg {
      transform: rotate(180deg);
      transition: 0.25s;
    }

    .subPanel {
      display: flex;
      color: black;

      .l1-list {
        display: flex;
        min-width: 10rem;
        flex-direction: column;
      }

      .l2-list {
        display: flex;
        flex-direction: column;
      }
    }
  }
}
</style>
